import { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import i18n from "@/i18n";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults — always send cookies for Google auth
  axios.defaults.withCredentials = true;

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Global 401 interceptor
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && (token || user)) {
          localStorage.removeItem("token");
          localStorage.removeItem("google_user");
          setToken(null);
          setUser(null);
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  // Fetch user on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const fetchUser = async () => {
      // Check for Google user in localStorage first (set by AuthCallback)
      const googleUser = localStorage.getItem("google_user");
      if (googleUser) {
        try {
          const res = await axios.get(`${API}/auth/me`);
          setUser(res.data);
          syncLang(res.data);
        } catch {
          localStorage.removeItem("google_user");
        }
        setLoading(false);
        return;
      }

      // Check JWT token
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        syncLang(response.data);
      } catch {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const syncLang = (userData) => {
    if (userData?.language_preference) {
      i18n.changeLanguage(userData.language_preference);
      localStorage.setItem('i18nextLng', userData.language_preference);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    syncLang(userData);

    return userData;
  };

  const register = async (email, password, name, userType) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      user_type: userType,
    });
    const { access_token, user: userData } = response.data;

    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);

    return userData;
  };

  const logout = async () => {
    // Clear Google session if exists
    try {
      await axios.post(`${API}/auth/google/logout`);
    } catch { /* ignore */ }
    localStorage.removeItem("token");
    localStorage.removeItem("google_user");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  const setGoogleUser = (userData) => {
    setUser(userData);
  };

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    setGoogleUser,
    isAuthenticated: !!user,
  }), [user, token, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
